import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Channels,
  MESSAGE_LIMITS,
  TEMPLATE_VARIABLES,
  formatRate,
  messages,
  renderTemplate,
  type Channel,
  type Template,
} from '@nesc/shared';
import { api, errorMessage, type SingleResponse } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Field } from '@/components/ui/field';
import { Input, Select, Textarea } from '@/components/ui/input';
import { SkeletonRows } from '@/components/ui/skeleton';
import { ConfirmDialog, EmptyState } from '@/components/common/feedback';
import { PageHeader } from '@/components/common/page';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

type Draft = {
  id?: string;
  name: string;
  channel: Channel;
  subjectTemplate: string;
  bodyTemplate: string;
};

const EMPTY: Draft = { name: '', channel: 'email', subjectTemplate: '', bodyTemplate: '' };

/** 見本の値で差し込み結果を確認する */
const SAMPLE = Object.fromEntries(TEMPLATE_VARIABLES.map((v) => [v.key, v.sample])) as Record<
  string,
  string
>;

/** S77 文面テンプレート。キャンペーンで「テンプレート差し込み」を選ぶとここの内容が使われる */
export const SettingsTemplatesPage = () => {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Draft | null>(null);
  const [removing, setRemoving] = useState<Template | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: qk.templates(),
    queryFn: () => api.get<SingleResponse<Template[]>>('/templates'),
  });

  const save = useMutation({
    mutationFn: (draft: Draft) => {
      const body = {
        name: draft.name,
        channel: draft.channel,
        subjectTemplate: draft.subjectTemplate || undefined,
        bodyTemplate: draft.bodyTemplate,
      };
      return draft.id ? api.patch(`/templates/${draft.id}`, body) : api.post('/templates', body);
    },
    onSuccess: () => {
      toast.success(messages.common.saved);
      void queryClient.invalidateQueries({ queryKey: ['templates'] });
      setEditing(null);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/templates/${id}`),
    onSuccess: () => {
      toast.success('テンプレートを削除しました');
      void queryClient.invalidateQueries({ queryKey: ['templates'] });
      setRemoving(null);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <>
      <PageHeader
        back="/settings"
        title={messages.template.title}
        description={messages.template.variableNote}
        actions={
          <Button onClick={() => setEditing(EMPTY)}>
            <Plus aria-hidden />
            {messages.template.create}
          </Button>
        }
      />

      {isLoading ? (
        <SkeletonRows rows={3} />
      ) : !data || data.data.length === 0 ? (
        <EmptyState
          title="テンプレートがありません"
          description="よく使う文面を登録すると、AI を使わずに企業名などを差し込んで送れます。"
          action={{ label: messages.template.create, onClick: () => setEditing(EMPTY) }}
        />
      ) : (
        <ul className="space-y-3">
          {data.data.map((template) => (
            <li key={template.id}>
              <Card>
                <CardHeader>
                  <div className="min-w-0">
                    <CardTitle className="flex flex-wrap items-center gap-2">
                      {template.name}
                      <Badge variant="dashed">{Channels.label(template.channel)}</Badge>
                    </CardTitle>
                    <p className="mt-1 text-note text-foreground-muted">
                      {template.subjectTemplate || '（件名なし）'}
                    </p>
                    {template.stats && template.stats.sentCompanies > 0 && (
                      <p className="tabular mt-1 text-note text-foreground-muted">
                        送信 {template.stats.sentCompanies} 社・返信{' '}
                        {template.stats.repliedCompanies} 社（{formatRate(template.stats.replyRate)}）
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setEditing({
                          id: template.id,
                          name: template.name,
                          channel: template.channel,
                          subjectTemplate: template.subjectTemplate ?? '',
                          bodyTemplate: template.bodyTemplate,
                        })
                      }
                    >
                      {messages.common.edit}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`${template.name} を削除する`}
                      onClick={() => setRemoving(template)}
                    >
                      <Trash2 aria-hidden />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <pre className="whitespace-pre-wrap break-words text-note text-foreground-muted">
                    {template.bodyTemplate.slice(0, 200)}
                    {template.bodyTemplate.length > 200 && '…'}
                  </pre>
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}

      <TemplateDialog
        draft={editing}
        onClose={() => setEditing(null)}
        onSave={(draft) => save.mutate(draft)}
        isPending={save.isPending}
      />

      <ConfirmDialog
        open={!!removing}
        onOpenChange={(open) => !open && setRemoving(null)}
        title="テンプレートを削除する"
        summary={`「${removing?.name}」を削除します。`}
        detail={<p className="text-body text-foreground-muted">{messages.template.inUse}</p>}
        confirmText={messages.common.delete}
        irreversible
        onConfirm={() => removing && remove.mutate(removing.id)}
        isPending={remove.isPending}
      />
    </>
  );
};

const TemplateDialog = ({
  draft,
  onClose,
  onSave,
  isPending,
}: {
  draft: Draft | null;
  onClose: () => void;
  onSave: (draft: Draft) => void;
  isPending: boolean;
}) => {
  const [form, setForm] = useState<Draft>(EMPTY);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (draft) setForm(draft);
  }, [draft]);

  const insert = (key: string) => {
    const textarea = bodyRef.current;
    const token = `{{${key}}}`;
    if (!textarea) {
      setForm((p) => ({ ...p, bodyTemplate: p.bodyTemplate + token }));
      return;
    }
    const start = textarea.selectionStart ?? form.bodyTemplate.length;
    const end = textarea.selectionEnd ?? start;
    const next = form.bodyTemplate.slice(0, start) + token + form.bodyTemplate.slice(end);
    setForm((p) => ({ ...p, bodyTemplate: next }));
    // 差し込んだ直後の位置にカーソルを戻す
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const maxBody =
    form.channel === 'email' ? MESSAGE_LIMITS.emailBodyMax : MESSAGE_LIMITS.formBodyMax;

  return (
    <Dialog open={!!draft} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="lg:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{form.id ? messages.template.edit : messages.template.create}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <Field label={messages.template.name} required>
              {(props) => (
                <Input
                  {...props}
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                />
              )}
            </Field>
            <Field label={messages.template.channel} required>
              {(props) => (
                <Select
                  {...props}
                  value={form.channel}
                  onChange={(e) => setForm((p) => ({ ...p, channel: e.target.value as Channel }))}
                >
                  {Channels.list.map((channel) => (
                    <option key={channel.value} value={channel.value}>
                      {channel.label}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>

          <Field
            label={messages.template.subject}
            count={{ used: form.subjectTemplate.length, max: MESSAGE_LIMITS.subjectMax }}
          >
            {(props) => (
              <Input
                {...props}
                value={form.subjectTemplate}
                onChange={(e) => setForm((p) => ({ ...p, subjectTemplate: e.target.value }))}
                placeholder="{{company_name}} 様へ {{product_name}} のご案内"
              />
            )}
          </Field>

          <div>
            <p className="mb-1.5 text-note text-foreground-muted">{messages.template.insertVariable}</p>
            <div className="flex flex-wrap gap-1.5">
              {TEMPLATE_VARIABLES.map((variable) => (
                <Button
                  key={variable.key}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => insert(variable.key)}
                >
                  {variable.label}
                </Button>
              ))}
            </div>
          </div>

          <Field
            label={messages.template.body}
            required
            count={{ used: form.bodyTemplate.length, max: maxBody }}
            hint={messages.template.variableNote}
          >
            {(props) => (
              <Textarea
                {...props}
                ref={bodyRef}
                rows={12}
                className="min-h-64"
                value={form.bodyTemplate}
                onChange={(e) => setForm((p) => ({ ...p, bodyTemplate: e.target.value }))}
              />
            )}
          </Field>

          <div>
            <p className="mb-1.5 text-note font-semibold">{messages.template.previewWithSample}</p>
            <div className="rounded-control border border-border bg-muted p-3">
              <p className="text-body font-semibold">
                {renderTemplate(form.subjectTemplate, SAMPLE) || '（件名なし）'}
              </p>
              <pre className="mt-2 whitespace-pre-wrap break-words text-body">
                {renderTemplate(form.bodyTemplate, SAMPLE) || '（本文なし）'}
              </pre>
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            {messages.common.cancel}
          </Button>
          <Button
            onClick={() => onSave(form)}
            disabled={isPending || !form.name.trim() || !form.bodyTemplate.trim()}
          >
            {messages.common.save}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
